import type { DatePickerProps } from 'components/shared/DatePicker/DatePicker';
import { DatePicker } from 'components/shared/DatePicker/DatePicker';
import type { DateCalculation } from 'components/shared/DatePicker/utils';
import { addDays, addMonths, addWeeks, addYears, startOfYear } from 'date-fns';
import type { IntlKey } from 'locales';
import { useMemo } from 'react';

export type MonoDatePickerProps = Omit<DatePickerProps<'mono'>, 'type' | 'values' | 'defaultValues'> & {
  value?: string | null;
  defaultValue?: string | null;
};
export function MonoDatePicker({ value, defaultValue, ...props }: MonoDatePickerProps) {
  const values = useMemo(() => [value], [value]);
  const defaultValues = useMemo(() => [defaultValue], [defaultValue]);
  return <DatePicker {...props} values={values} defaultValues={defaultValues} type="mono" />;
}

const defaultDatePresetKeys: IntlKey[] = [
  'date.presets.today',
  'date.presets.threeMonths',
  'date.presets.sixMonths',
  'date.presets.nineMonths',
  'date.presets.oneYear',
  'date.presets.twoYears',
  'date.presets.threeYears',
  'date.presets.fiveYears',
  'date.presets.tenYears',
];

const defaultDatePresets: DateCalculation[] = [
  today => today,
  today => addMonths(today, 3),
  today => addMonths(today, 6),
  today => addMonths(today, 9),
  today => addYears(today, 1),
  today => addYears(today, 2),
  today => addYears(today, 3),
  today => addYears(today, 5),
  today => addYears(today, 10),
];

export function MonoDatePickerWithDefaultPreset(props: MonoDatePickerProps) {
  return <MonoDatePicker {...props} datePresets={defaultDatePresets} datePresetTranslations={defaultDatePresetKeys} />;
}

export function MonoDatePickerWithCustomPreset(props: MonoDatePickerProps) {
  const { datePresets, datePresetTranslations } = props;
  return <MonoDatePicker {...props} datePresets={datePresets} datePresetTranslations={datePresetTranslations} />;
}

const blotterDefaultDatePresetKeys: IntlKey[] = [
  'date.presets.today',
  'date.presets.yesterday',
  'date.presets.oneWeekAgo',
  'date.presets.oneMonthAgo',
  'date.presets.threeMonthsAgo',
  'date.presets.sixMonthsAgo',
  'date.presets.beginningOfYear',
  'date.presets.oneYearAgo',
];

const blotterDefaultDatePresets: DateCalculation[] = [
  today => today,
  today => addDays(today, -1),
  today => addWeeks(today, -1),
  today => addMonths(today, -1),
  today => addMonths(today, -3),
  today => addMonths(today, -6),
  today => startOfYear(today),
  today => addYears(today, -1),
];

export function BlotterMonoDatePicker(props: MonoDatePickerProps) {
  return (
    <MonoDatePicker
      {...props}
      datePresets={blotterDefaultDatePresets}
      datePresetTranslations={blotterDefaultDatePresetKeys}
      tabIndex={props.tabIndex}
    />
  );
}
